import type { Environment } from "../../types";
import type {
  GlobalMenuCategory,
  GlobalMenuItem,
  GlobalMenuItemAssetUrl,
  GlobalMenuItemServiceStatus,
} from "./types";

export type AppLauncherStatusId = GlobalMenuCategory["status"];

export interface AppLauncherStatusSection extends GlobalMenuCategory {}

export interface AppLauncherGroupedStatusSection extends AppLauncherStatusSection {
  items: GlobalMenuItem[];
}

export const APP_LAUNCHER_STATUS_SECTIONS: AppLauncherStatusSection[] = [
  {
    status: "all",
    label: "All",
  },
  {
    status: "ending",
    label: "Ending Soon",
  },
];

const APP_LAUNCHER_STATUS_IDS = new Set<AppLauncherStatusId>([
  "all",
  "available",
  "ending",
  "comingSoon",
]);

export function getAppLauncherStatusSections(
  categories?: readonly GlobalMenuCategory[] | null,
): AppLauncherStatusSection[] {
  const configuredSections =
    categories
      ?.filter((category): category is AppLauncherStatusSection => {
        return (
          APP_LAUNCHER_STATUS_IDS.has(category.status) &&
          typeof category.label === "string" &&
          category.label.trim().length > 0
        );
      })
      .map((category) => ({
        status: category.status,
        label: category.label.trim(),
      })) ?? [];

  return configuredSections.length > 0
    ? configuredSections
    : APP_LAUNCHER_STATUS_SECTIONS;
}

export function getGlobalMenuItemStatus(
  item: GlobalMenuItem,
): GlobalMenuItemServiceStatus {
  if (item.serviceStatus === "ending" || item.serviceStatus === "comingSoon") {
    return item.serviceStatus;
  }
  return "available";
}

export function resolveGlobalMenuItemAssetUrl(
  assetUrl: GlobalMenuItemAssetUrl,
  env: Environment,
): string {
  if (typeof assetUrl === "string") {
    return assetUrl;
  }

  return assetUrl[env] ?? assetUrl.production ?? assetUrl.stage ?? assetUrl.dev;
}

export function getSortedGlobalMenuItems(
  items: readonly GlobalMenuItem[],
): GlobalMenuItem[] {
  return [...items].sort((a, b) => {
    const statusDelta = getAllMenuSortRank(a) - getAllMenuSortRank(b);
    if (statusDelta !== 0) return statusDelta;
    return a.order - b.order;
  });
}

export function groupGlobalMenuItemsByStatus(
  items: GlobalMenuItem[],
  sections: readonly AppLauncherStatusSection[] = APP_LAUNCHER_STATUS_SECTIONS,
): AppLauncherGroupedStatusSection[] {
  const sortedItems = getSortedGlobalMenuItems(items);

  return getAppLauncherStatusSections(sections).map((section) => ({
    ...section,
    items:
      section.status === "all"
        ? sortedItems
        : sortedItems.filter(
            (item) => getGlobalMenuItemStatus(item) === section.status,
          ),
  })).filter((section) => section.items.length > 0);
}

function getAllMenuSortRank(item: GlobalMenuItem): number {
  const status = getGlobalMenuItemStatus(item);
  if (status === "comingSoon") return 0;
  if (status === "ending") return 2;
  return 1;
}
